import { useEffect, useState } from 'react';

/**
 * Whether a media query matches, kept current as the screen changes: a window
 * dragged narrower, a phone turned on its side.
 *
 * The phone layout kept this to itself, in `usePhoneLayout`. The enlarged teeth
 * chart asks a second question of the screen (a phone lying in landscape), so
 * the logic lives here for any query and the phone layout calls it. The request
 * of 5 October 2026 (third), section 5.
 *
 * jsdom, the print pipeline and a server render have no `matchMedia`, and all
 * three answer false, which for the phone query is the desktop every screen
 * drew before there was a phone layout at all. A test that wants a match stubs
 * `matchMedia`, as the chart's phone test does.
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => matchesNow(query));

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const list = window.matchMedia(query);
    const update = () => setMatches(list.matches);
    update();
    list.addEventListener('change', update);
    return () => list.removeEventListener('change', update);
  }, [query]);

  return matches;
}

function matchesNow(query: string): boolean {
  return typeof window !== 'undefined' && typeof window.matchMedia === 'function'
    ? window.matchMedia(query).matches
    : false;
}
